/**
 * Service pour gérer les styles personnalisés des widgets
 */

import { css, CSSResult, unsafeCSS } from "lit";
import { WidgetVisualCustomization, WidgetFont } from "../types/widgets.d";

export class StyleService {
  /**
   * Génère les variables CSS personnalisées pour un widget
   */
  static generateCSSVariables(visual: WidgetVisualCustomization): string {
    // Convertir la couleur primaire en RGB pour créer une version avec opacité
    const primaryColor = visual.colors.primary;
    const rgb = this.hexToRgb(primaryColor);
    const primaryColorRgba = rgb
      ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`
      : `rgba(0, 148, 219, 0.2)`;

    // Lors de la création d'un widget dans le BO, on met par défaut ces 2 valeurs de taille à null pour éviter les problèmes, et les champs sont non modifiables
    const titleSizeVar =
      visual.typography.titleSize != null
        ? `--widget-title-size: ${visual.typography.titleSize}px;`
        : "";
    const textSizeVar =
      visual.typography.textSize != null
        ? `--widget-text-size: ${visual.typography.textSize}px;`
        : "";

    return `
      --widget-primary-color: ${primaryColor};
      --widget-primary-color-rgba-20: ${primaryColorRgba};
      --widget-secondary-color: ${visual.colors.secondary};
      --widget-background-color: "#f8f9fa";
      --widget-card-background-color: "#ffffff";
      --widget-title-font-family: ${this.getFontFamily(visual.typography.titleFont) || "Inter, sans-serif"};
      ${titleSizeVar}
      --widget-title-color: ${visual.typography.titleColor || "#2F2F2F"};
      --widget-text-font-family: ${this.getFontFamily(visual.typography.textFont) || "Inter, sans-serif"};
      ${textSizeVar}
      --widget-text-color: ${visual.typography.textColor || "#2F2F2F"};
    `;
  }

  /**
   * Convertit une couleur hexadécimale en RGB
   */
  private static hexToRgb(
    hex: string,
  ): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  /**
   * Génère le style CSS personnalisé complet
   */
  static generateCustomStyles(visual: WidgetVisualCustomization): CSSResult {
    const variables = this.generateCSSVariables(visual);

    let customCSS = "";
    if (visual.customCSS) {
      customCSS = visual.customCSS;
    }

    return css`
      :host {
        ${unsafeCSS(variables)}
      }
      ${unsafeCSS(customCSS)}
    `;
  }

  /**
   * Retourne la font-family CSS appropriée.
   * Arial et Helvetica sont des polices système (pas chargées via Google Fonts).
   */
  static getFontFamily(font: WidgetFont | string | undefined): string {
    if (font === WidgetFont.INHERIT || font === "inherit") {
      return "inherit";
    }
    if (font == null || font === "") {
      return '"Inter", sans-serif';
    }

    const raw = typeof font === "string" ? font.trim() : String(font);

    // Map des polices (Google Fonts + polices système Arial/Helvetica)
    const fontFamilies: Record<string, string> = {
      [WidgetFont.ROBOTO]: '"Roboto", sans-serif',
      [WidgetFont.OPEN_SANS]: '"Open Sans", sans-serif',
      [WidgetFont.INTER]: '"Inter", sans-serif',
      [WidgetFont.MONTSERRAT]: '"Montserrat", sans-serif',
      [WidgetFont.LATO]: '"Lato", sans-serif',
      [WidgetFont.POPPINS]: '"Poppins", sans-serif',
      [WidgetFont.DM_SANS]: '"DM Sans", sans-serif',
      [WidgetFont.HELVETICA]: '"Helvetica", sans-serif',
      [WidgetFont.ARIAL]: '"Arial", sans-serif',
    };

    // Recherche exacte puis insensible à la casse (config API peut envoyer "Arial", "arial", etc.)
    const exact = fontFamilies[raw];
    if (exact) return exact;
    const key = Object.keys(fontFamilies).find(
      (k) => k.toLowerCase() === raw.toLowerCase(),
    );
    return (key && fontFamilies[key]) ?? '"Inter", sans-serif';
  }

  /**
   * Charge les polices Google Fonts si nécessaire (API CSS2, format wght@400;600;700)
   */
  static loadGoogleFonts(fonts: WidgetFont[]): void {
    const googleFonts = [
      WidgetFont.ROBOTO,
      WidgetFont.OPEN_SANS,
      WidgetFont.INTER,
      WidgetFont.MONTSERRAT,
      WidgetFont.LATO,
      WidgetFont.POPPINS,
      WidgetFont.DM_SANS,
    ];

    const fontsToLoad = [
      ...new Set(fonts.filter((f) => googleFonts.includes(f))),
    ];
    if (fontsToLoad.length === 0) return;

    // Vérifier si les polices sont déjà chargées
    const existingLink = document.querySelector("link[data-apidae-fonts]");
    if (existingLink) return;

    // Format CSS2 : family=Inter:wght@400;600;700 (pas l'ancien :400,600,700 qui peut provoquer CORB)
    const familyParams = fontsToLoad.map((f) => {
      const name = f.replace(/ /g, "+");
      return `family=${name}:wght@400;500;600;700`;
    });

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${familyParams.join("&")}&display=swap`;
    link.setAttribute("data-apidae-fonts", "true");
    document.head.appendChild(link);
  }
}
